import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import pluralize from 'pluralize';
import { Button, BiContainer, Switch, TableList, Tooltip, Typography } from 'seagull';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { NotificationSettingsEntryType, NotificationStatus } from '~/models/NotificationSettingsModel';
import { selectors } from '~/store/state-selectors';
import { deriveIsActive, getNotificationsToShow, getRecipientTypesText, isDays, minutesToDays, notificationSettingsTextsPath, Table } from '../../NotificationSettings.utils';
import styles from './NotificationSettingsTableV2.module.scss';
import { GetNotificationSettingsTableV2TID } from './NotificationSettingsTableV2.testIds';
const typeMap = {
    [Table.Reminders]: NotificationSettingsEntryType.Recurring,
    [Table.Notifications]: NotificationSettingsEntryType.Single
};
// Status column grows to fill remaining row width so the toggle stays flush right; min ensures it
// fits the Edit pill + toggle even on narrow tables.
const STATUS_COLUMN_MIN_WIDTH = 200;
const STATUS_COLUMN_MAX_WIDTH = 800;
const NOTIFICATION_COLUMN_DEFS = [
    {
        width: 360
    },
    {
        width: 280
    },
    {
        width: [
            STATUS_COLUMN_MIN_WIDTH,
            STATUS_COLUMN_MAX_WIDTH
        ],
        hAlign: 'end'
    }
];
const REMINDER_COLUMN_DEFS = [
    {
        width: 360
    },
    {
        width: 220
    },
    {
        width: 100
    },
    {
        width: [
            STATUS_COLUMN_MIN_WIDTH,
            STATUS_COLUMN_MAX_WIDTH
        ],
        hAlign: 'end'
    }
];
const formatDelay = (minutes)=>isDays(minutes) ? pluralize('day', minutesToDays(minutes), true) : pluralize('minute', minutes, true);
export function NotificationSettingsTableV2(param) {
    let { data, tableType, selectedTab, onEditClick, onToggleClick, testId = "".concat(tableType, "-table-v2") } = param;
    const { NotificationSettingsTableV2TestIds: testIds } = useMemo(()=>GetNotificationSettingsTableV2TID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys(notificationSettingsTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const featureFlags = useFeatureFlags();
    const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
    const showClientHubIntegration = useStore(selectors.integration.showClientHubIntegration);
    const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
    const showKeeperIntegration = useStore(selectors.integration.showKeeperIntegration);
    const notificationsTextMap = getNotificationsToShow({
        tab: selectedTab,
        showKarbonIntegration,
        showClientHubIntegration,
        showFinancialCentsIntegration,
        showKeeperIntegration,
        featureFlags,
        texts
    });
    const isReminderTable = tableType === Table.Reminders;
    const columnDefs = isReminderTable ? REMINDER_COLUMN_DEFS : NOTIFICATION_COLUMN_DEFS;
    const headerItems = isReminderTable ? [
        {
            value: texts.tableHeaders.delayedAction()
        },
        {
            value: texts.tableHeaders.notifiedTeamMember()
        },
        {
            value: texts.tableHeaders.delay()
        },
        {
            value: texts.tableHeaders.status()
        }
    ] : [
        {
            value: texts.tableHeaders.eventName()
        },
        {
            value: texts.tableHeaders.notifiedTeamMember()
        },
        {
            value: texts.tableHeaders.status()
        }
    ];
    return /*#__PURE__*/ _jsx(BiContainer, {
        name: "notification_settings_v2",
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: columnDefs,
            children: [
                /*#__PURE__*/ _jsx(TableList.Header, {
                    items: headerItems,
                    testId: testIds.Root(),
                    className: styles.header
                }),
                Object.entries(notificationsTextMap).map((param)=>{
                    let [key, label] = param;
                    const tabSettings = data[selectedTab];
                    const notification = tabSettings[key];
                    if (!notification || notification.type !== typeMap[tableType]) {
                        return null;
                    }
                    const notificationName = key;
                    const editability = notification.editability;
                    const isAlwaysOn = editability.recipients === false;
                    const isToggleOn = isAlwaysOn || deriveIsActive(notification);
                    // Recipients survive toggle-off server-side now, but the column should still
                    // appear blank when the toggle is off — otherwise the row looks like it's still
                    // notifying someone. Recipients reappear when the user toggles back on.
                    const recipientLabels = isToggleOn ? getRecipientTypesText({
                        recipientTypes: notification.recipientTypes,
                        notificationName,
                        texts
                    }) : [];
                    const joinedRecipients = recipientLabels.join(', ');
                    const tooltipText = texts.contentCustomization.alwaysOnTooltip();
                    // Always-on entries (recipients=false) cannot be toggled.
                    const isToggleDisabled = isAlwaysOn;
                    const showEditPill = editability.content || editability.recipients;
                    const switchTooltip = isAlwaysOn ? {
                        title: tooltipText
                    } : undefined;
                    return /*#__PURE__*/ _jsx(TableList.Row, {
                        size: "fluid",
                        className: styles.row,
                        testId: testIds.Row(notificationName),
                        items: [
                            {
                                value: /*#__PURE__*/ _jsx(Typography, {
                                    weight: "normal",
                                    testId: testIds.EventName(notificationName),
                                    children: label
                                })
                            },
                            {
                                value: /*#__PURE__*/ _jsx(Tooltip, {
                                    title: joinedRecipients,
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        className: styles.recipientLabel,
                                        testId: testIds.Recipients(notificationName),
                                        children: joinedRecipients
                                    })
                                })
                            },
                            ...isReminderTable && notification.type === NotificationSettingsEntryType.Recurring ? [
                                {
                                    value: /*#__PURE__*/ _jsx(Typography, {
                                        testId: testIds.Delay(notificationName),
                                        children: formatDelay(notification.recurring.minutes)
                                    })
                                }
                            ] : [],
                            {
                                value: /*#__PURE__*/ _jsxs("div", {
                                    className: styles.statusCell,
                                    children: [
                                        showEditPill && /*#__PURE__*/ _jsx(Button, {
                                            size: "small",
                                            bi: {
                                                name: 'edit_notification',
                                                data: {
                                                    notificationName
                                                }
                                            },
                                            testId: testIds.EditPill(notificationName),
                                            className: styles.editPill,
                                            onClick: ()=>onEditClick({
                                                    notification,
                                                    notificationName,
                                                    notificationLabel: label
                                                }),
                                            children: commonTexts.buttons.edit()
                                        }),
                                        /*#__PURE__*/ _jsx(Switch, {
                                            checked: isToggleOn,
                                            disabled: isToggleDisabled,
                                            tooltip: switchTooltip,
                                            bi: {
                                                name: 'notification_enabled_toggle',
                                                data: {
                                                    notificationName
                                                }
                                            },
                                            testId: testIds.Toggle(notificationName),
                                            onChange: (_, checked)=>onToggleClick({
                                                    notification,
                                                    notificationName,
                                                    nextStatus: checked ? NotificationStatus.Active : NotificationStatus.Silent
                                                })
                                        })
                                    ]
                                })
                            }
                        ]
                    }, key);
                })
            ]
        })
    });
}
